/**
 * Updates the Kiyoh score and review count spans (data-kiyoh-score / data-kiyoh-count)
 * with live values from the public Kiyoh widget endpoint. Reads a 24h localStorage
 * cache first. On any failure (network, timeout, parsing, validation, storage) the
 * server-rendered fallback text stays untouched.
 */
(function kiyohScore() {
  const scoreElements = document.querySelectorAll('[data-kiyoh-score]');
  const countElements = document.querySelectorAll('[data-kiyoh-count]');
  if (!scoreElements.length && !countElements.length) return;

  const scriptElement = document.currentScript;
  const locationId = scriptElement?.dataset.kiyohLocation || '1046008';
  const cacheKey = `kiyoh-rating-${locationId}`;
  const cacheTtl = 24 * 60 * 60 * 1000;
  const requestTimeout = 4000;

  const isValidScore = (value) => {
    const match = /^(10(?:[.,]0)?|[1-9](?:[.,]\d)?)$/.exec(value);
    if (!match) return false;
    const normalized = parseFloat(value.replace(',', '.'));
    return normalized >= 1 && normalized <= 10;
  };

  const isValidCount = (value) => /^[1-9]\d*$/.test(value);

  // Leading integer with optional thousands separators (dot, comma or whitespace,
  // which includes nbsp) in groups of 3, followed by a word, e.g. "1695 beoordelingen"
  // or "1.695 reviews". Rejects anything else, such as "1.5 reviews".
  const rawCountPattern = /^(\d+|\d{1,3}(?:[.,\s]\d{3})+)\s+\S+$/;

  const applyValues = (score, count) => {
    scoreElements.forEach((element) => {
      element.textContent = score;
    });
    countElements.forEach((element) => {
      element.textContent = count;
    });
  };

  const readCache = () => {
    try {
      const raw = localStorage.getItem(cacheKey);
      if (!raw) return null;
      const cached = JSON.parse(raw);
      if (
        !cached ||
        typeof cached.score !== 'string' ||
        typeof cached.count !== 'string' ||
        typeof cached.timestamp !== 'number'
      ) {
        return null;
      }
      const age = Date.now() - cached.timestamp;
      if (
        !isValidScore(cached.score) ||
        !isValidCount(cached.count) ||
        !Number.isFinite(cached.timestamp) ||
        age < 0 ||
        age >= cacheTtl
      ) {
        return null;
      }
      return cached;
    } catch (error) {
      return null;
    }
  };

  const writeCache = (score, count) => {
    try {
      localStorage.setItem(cacheKey, JSON.stringify({ score, count, timestamp: Date.now() }));
    } catch (error) {
      // Storage unavailable (private mode, quota, ...): the freshly fetched values
      // stay applied on the page for this load, just not cached for next time.
    }
  };

  const cached = readCache();
  if (cached) {
    applyValues(cached.score, cached.count);
    return;
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), requestTimeout);

  const widgetUrl =
    'https://www.kiyoh.com/retrieve-widget.html?color=white&allowTransparency=false&button=false&lang=nl&tenantId=98&locationId=' +
    encodeURIComponent(locationId);

  fetch(widgetUrl, { signal: controller.signal })
    .then((response) => {
      if (!response.ok) throw new Error('Kiyoh widget request failed');
      return response.text();
    })
    .then((html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const scoreElement = doc.querySelector('.rating');
      const countElement = doc.querySelector('.review-count');
      if (!scoreElement || !countElement) return;

      const score = scoreElement.textContent.trim();
      const rawCount = countElement.textContent.trim();
      const countMatch = rawCountPattern.exec(rawCount);
      if (!countMatch) return;
      const count = countMatch[1].replace(/[.,\s]/g, '');

      if (!isValidScore(score) || !isValidCount(count)) return;

      const formattedScore = score.replace('.', ',');
      applyValues(formattedScore, count);
      writeCache(formattedScore, count);
    })
    .catch(() => {
      // Network error, timeout or invalid data: keep the server-rendered fallback.
    })
    .finally(() => clearTimeout(timeoutId));
})();
